import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Animation for the sparkle rotation - rotates briefly every 15 seconds
const rotateIntermittent = keyframes`
  0% { transform: rotate(0deg); }
  5% { transform: rotate(360deg); }
  100% { transform: rotate(360deg); }
`;

// Continuous rotation animation
const rotateContinuous = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Pulse animation for thinking state
const pulse = keyframes`
  0% { 
    transform: scale(1);
    opacity: 0.7;
  }
  50% { 
    transform: scale(1.1);
    opacity: 1;
  }
  100% { 
    transform: scale(1);
    opacity: 0.7;
  }
`;

/**
 * Reusable Sparkle/Star Icon Component
 * Used for AI thinking indicators and decorative elements
 */
const SparkleIcon = ({
  size = 24,
  animate = 'rotate', // 'rotate', 'rotateContinuous', 'pulse', or 'none'
  glass = false,
  colors,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const gradientColors = colors || {
    start: theme.palette.modern.blue[400],
    middle: theme.palette.secondary.main,
    end: theme.palette.modern.mainColor[300]
  };
  const animationMap = {
    rotate: `${rotateIntermittent} 15s ease-in-out infinite`,
    rotateContinuous: `${rotateContinuous} 2s linear infinite`,
    pulse: `${pulse} 1.5s ease-in-out infinite`,
    none: 'none'
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: animationMap[animate],
        transition: 'transform 0.2s ease-in-out',
        // ...(glass && {
        //   borderRadius: '50%',
        //   background: 'linear-gradient(145deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.14) 100%)',
        //   border: '1px solid rgba(255,255,255,0.42)',
        //   backdropFilter: 'blur(8px) saturate(140%)',
        //   WebkitBackdropFilter: 'blur(8px) saturate(140%)',
        //   boxShadow: '0 8px 20px rgba(15, 23, 42, 0.26), inset 0 1px 1px rgba(255,255,255,0.55)',
        // }),
        ...sx
      }}
      {...props}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 32 32" 
        style={{ 
          width: '100%', 
          height: '100%'
        }}
      >
        <defs>
          <linearGradient id={`starGradient-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradientColors.start} />
            <stop offset="50%" stopColor={gradientColors.middle} />
            <stop offset="100%" stopColor={gradientColors.end} />
          </linearGradient>
        </defs>
        <path 
          fill={`url(#starGradient-${size})`} 
          d="M-3.9,-84.95 C-5.28,-79.47 -7.08,-74.14 -9.32,-68.93 C-15.16,-55.37 -23.16,-43.5 -33.33,-33.33 C-43.5,-23.17 -55.37,-15.16 -68.93,-9.32 C-74.13,-7.08 -79.47,-5.28 -84.95,-3.9 C-86.74,-3.45 -88,-1.85 -88,0 C-88,1.85 -86.74,3.45 -84.95,3.9 C-79.47,5.28 -74.14,7.08 -68.93,9.32 C-55.37,15.16 -43.51,23.16 -33.33,33.33 C-23.16,43.5 -15.15,55.37 -9.32,68.93 C-7.08,74.13 -5.28,79.47 -3.9,84.95 C-3.45,86.74 -1.84,88 0,88 C1.85,88 3.45,86.74 3.9,84.95 C5.28,79.47 7.08,74.14 9.32,68.93 C15.16,55.37 23.16,43.51 33.33,33.33 C43.5,23.16 55.37,15.15 68.93,9.32 C74.13,7.08 79.47,5.28 84.95,3.9 C86.74,3.45 88,1.84 88,0 C88,-1.85 86.74,-3.45 84.95,-3.9 C79.47,-5.28 74.14,-7.08 68.93,-9.32 C55.37,-15.16 43.51,-23.16 33.33,-33.33 C23.16,-43.5 15.15,-55.37 9.32,-68.93 C7.08,-74.13 5.28,-79.47 3.9,-84.95 C3.45,-86.74 1.85,-88 0,-88 C-1.85,-88 -3.45,-86.74 -3.9,-84.95z"
          transform="translate(16, 16) scale(0.15)"
        />
      </svg>
    </Box>
  );
};

export default SparkleIcon;